import { useCallback, useMemo } from 'react';

import type { CharacterMeshFormat } from '@cartwhl/3d/player';
import type {
  LoadFBXMesh,
  TransformLoadedMesh,
} from '@cartwhl/3d/r3f/hooks/useCharacterMeshWithCredentials';
import { resolveCharacterMeshFormat } from '@cartwhl/3d/r3f/characterMeshFormat';
import {
  createFbxCharacterMeshTransform,
  createLoadFbxCharacterMesh,
} from '../../../shared/cartographerFbxCharacterMesh';

import { collectSceneAssetStats } from '../../lib/assetHygiene';
import { pushCartographerDebugEvent } from '../../lib/cartographerDebug';

export interface UseCartographerFbxMeshOptionsParams {
  meshPath: string;
  meshFormat?: CharacterMeshFormat | null;
  extensionHint?: string | null;
  meshFile?: File | null;
  /** When false, skips Cartographer debug telemetry. */
  enableDebugEvents?: boolean;
}

export function useCartographerFbxMeshOptions({
  meshPath,
  meshFormat,
  extensionHint,
  meshFile = null,
  enableDebugEvents = true,
}: UseCartographerFbxMeshOptionsParams) {
  const resolvedMeshFormat = useMemo(
    () =>
      resolveCharacterMeshFormat(meshPath, extensionHint, meshFormat ?? undefined),
    [extensionHint, meshFormat, meshPath],
  );

  const loadFBX = useMemo<LoadFBXMesh | undefined>(
    () =>
      resolvedMeshFormat === 'fbx'
        ? createLoadFbxCharacterMesh(meshFile)
        : undefined,
    [meshFile, resolvedMeshFormat],
  );

  const transformLoadedMesh = useCallback<TransformLoadedMesh>(
    async (meshData) => {
      if (resolvedMeshFormat !== 'fbx') return;

      const transformStartedAt = performance.now();
      await createFbxCharacterMeshTransform({ meshPath, meshFile })(meshData);

      if (!enableDebugEvents) return;

      pushCartographerDebugEvent('motionPreview.mesh.asset.stats', {
        meshPath,
        meshFormat: resolvedMeshFormat,
        transformDurationMs: Number(
          (performance.now() - transformStartedAt).toFixed(2),
        ),
        afterStats: collectSceneAssetStats(meshData.scene),
      });
    },
    [enableDebugEvents, meshFile, meshPath, resolvedMeshFormat],
  );

  return useMemo(
    () => ({
      meshFormat: resolvedMeshFormat,
      loadFBX,
      transformLoadedMesh:
        resolvedMeshFormat === 'fbx' ? transformLoadedMesh : undefined,
    }),
    [loadFBX, resolvedMeshFormat, transformLoadedMesh],
  );
}
